<template>
  <div>
    <!-- Enrollment selector (always visible so user can switch within the tab) -->
    <v-row class="mb-2">
      <v-col cols="12" md="6">
        <v-autocomplete
          v-model="localEnrolmentId"
          :items="enrolments"
          item-value="enrolment_id"
          label="Select Enrolment to View Progress"
          outlined
          dense
          clearable
          prepend-inner-icon="mdi-account-group"
          @change="onLocalEnrolmentChange"
        >
          <template v-slot:item="{ item }">
            <span>
              <strong>L{{ item.learner_id }}</strong
              >&nbsp;–&nbsp;{{ item.learner }}
              <span class="grey--text ml-2"
                >| <strong>A{{ item.adi_id }}</strong
                >&nbsp;–&nbsp;{{ item.adi }}</span
              >
            </span>
          </template>
          <template v-slot:selection="{ item }">
            L{{ item.learner_id }} – {{ item.learner }} | A{{ item.adi_id }} –
            {{ item.adi }}
          </template>
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>No enrolments available.</v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <template v-if="activeEnrolment">
      <!-- Learner summary banner -->
      <v-alert color="primary" dark dense class="mb-4">
        <v-row no-gutters align="center">
          <v-col>
            <v-icon left small>mdi-account</v-icon>
            <strong>L{{ activeEnrolment.learner_id }}</strong
            >&nbsp;–&nbsp;{{ activeEnrolment.learner }}
            <span class="mx-2">|</span>
            <v-icon left small>mdi-account-tie</v-icon>
            <strong>A{{ activeEnrolment.adi_id }}</strong
            >&nbsp;–&nbsp;{{ activeEnrolment.adi }}
          </v-col>
          <v-col cols="auto">
            <v-chip small dark outlined>
              Enrolment #{{ activeEnrolment.enrolment_id }}
            </v-chip>
          </v-col>
        </v-row>
      </v-alert>

      <!-- Record Progress form -->
      <v-card outlined class="mb-6">
        <v-card-title class="subtitle-1 font-weight-bold grey lighten-4">
          <v-icon left>mdi-pencil-plus-outline</v-icon>
          Record Progress
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form @submit.prevent="submitProgress">
            <v-row>
              <v-col cols="12" md="3">
                <v-select
                  v-model="recordForm.skillId"
                  :items="availableSkills"
                  item-text="skill_name"
                  item-value="skill_id"
                  label="Skill *"
                  outlined
                  dense
                  hide-no-data
                  :error-messages="recordSkillErrors"
                  @change="onRecordSkillChange"
                  @blur="$v.recordForm.skillId.$touch()"
                >
                  <template v-slot:item="{ item }">
                    {{ item.skill_number }}. {{ item.skill_name }}
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.skill_number }}. {{ item.skill_name }}
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="recordForm.stepId"
                  :items="availableSteps"
                  item-text="step_name"
                  item-value="step_id"
                  label="Step *"
                  outlined
                  dense
                  hide-no-data
                  :disabled="!recordForm.skillId"
                  :error-messages="recordStepErrors"
                  @blur="$v.recordForm.stepId.$touch()"
                >
                  <template v-slot:item="{ item }">
                    {{ item.step_number }}. {{ item.step_name }}
                    <v-chip
                      v-if="item.status && statusConfig[item.status]"
                      x-small
                      class="ml-2"
                      :color="statusConfig[item.status].chipColor"
                      dark
                    >
                      {{ item.status }}
                    </v-chip>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.step_number }}. {{ item.step_name }}
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" md="2">
                <v-menu
                  v-model="sessionDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="recordForm.sessionDate"
                      label="Session Date *"
                      readonly
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar"
                      v-on="on"
                      :error-messages="recordDateErrors"
                      @blur="$v.recordForm.sessionDate.$touch()"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="recordForm.sessionDate"
                    locale="en-gb"
                    :max="todayDate"
                    no-title
                    @input="
                      sessionDateMenu = false;
                      $v.recordForm.sessionDate.$touch();
                    "
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" md="2">
                <v-select
                  v-model="recordForm.status"
                  :items="statusOptions"
                  label="Progress Status *"
                  outlined
                  dense
                  :error-messages="recordStatusErrors"
                  @blur="$v.recordForm.status.$touch()"
                >
                  <template v-slot:item="{ item }">
                    <v-icon left small :color="statusConfig[item].color">
                      {{ statusConfig[item].icon }}
                    </v-icon>
                    {{ item }}
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" md="2">
                <v-textarea
                  v-model.trim="recordForm.notes"
                  label="Notes (optional)"
                  outlined
                  dense
                  auto-grow
                  rows="1"
                  counter="500"
                  :error-messages="recordNotesErrors"
                  @input="$v.recordForm.notes.$touch()"
                  @blur="$v.recordForm.notes.$touch()"
                ></v-textarea>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-btn
                  color="primary"
                  type="submit"
                  :loading="recordSubmitting"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Save Progress
                </v-btn>
                <v-btn
                  text
                  class="ml-2"
                  @click="resetRecordForm"
                  :disabled="recordSubmitting"
                >
                  Clear
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>

      <!-- Progress accordion -->
      <v-progress-linear
        v-if="progressLoading"
        indeterminate
        color="primary"
        class="mb-4"
      ></v-progress-linear>

      <template v-if="!progressLoading && groupedProgress.length">
        <div class="mb-3">
          <div class="d-flex align-center mb-3">
            <span class="subtitle-1 font-weight-bold">
              Syllabus Progress — {{ progressData[0].syllabus_name }}
              <v-chip x-small outlined color="primary" class="ml-1">
                {{ progressData[0].syllabus_version }}
              </v-chip>
            </span>
            <v-spacer></v-spacer>
            <span class="caption grey--text">
              {{ overallCompleted }} / {{ overallTotal }} steps completed
            </span>
          </div>

          <!-- Status Progress Timeline -->
          <!-- <div class="mb-4">
            <div class="caption font-weight-bold mb-2">Progress Levels</div>
            <div class="d-flex align-center" style="position: relative">
              <template v-for="(status, idx) in progressStatuses">
                <div
                  :key="`status-${idx}`"
                  class="d-flex flex-column align-center"
                  style="flex: 1; position: relative"
                >
                  
                  <v-icon
                    small
                    :color="statusConfig[status].color"
                    style="font-size: 24px; z-index: 2; position: relative"
                  >
                    mdi-circle
                  </v-icon>
                 
                  <div
                    class="caption text-center mt-1"
                    style="font-size: 10px; line-height: 1.2; font-weight: 500"
                  >
                    {{ status }}
                  </div>
                </div>

              
                <div
                  v-if="idx < progressStatuses.length - 1"
                  :key="`status-line-${idx}`"
                  class="flex-grow-1"
                  style="
                    height: 3px;
                    margin: 0 -8px;
                    z-index: 1;
                    background-color: #e0e0e0;
                  "
                ></div>
              </template>
            </div>
          </div> -->

          <!-- Status Legend (Icon Reference) -->
          <div class="caption font-weight-bold mb-2">Status Icons</div>
          <v-row
            no-gutters
            class="pa-3"
            style="background-color: #f5f5f5; border-radius: 4px"
          >
            <v-col
              v-for="status in progressStatuses"
              :key="`legend-${status}`"
              cols="12"
              sm="6"
              md="4"
              lg="2"
              class="d-flex align-center px-2 py-1"
            >
              <v-icon small :color="statusConfig[status].color" class="mr-1">
                {{ statusConfig[status].icon }}
              </v-icon>
              <span class="caption font-weight-medium">{{ status }}</span>
            </v-col>
          </v-row>
        </div>

        <v-expansion-panels accordion>
          <v-expansion-panel
            v-for="skill in groupedProgress"
            :key="skill.skill_id"
          >
            <v-expansion-panel-header class="px-4 py-3">
              <v-row no-gutters align="center" class="pr-6">
                <v-col>
                  <span class="font-weight-medium">
                    {{ skill.skill_number }}. {{ skill.skill_name }}
                  </span>
                </v-col>

                <v-col cols="auto" class="mx-3">
                  <v-chip
                    x-small
                    :color="statusConfig[skill.bestStatus].chipColor"
                    dark
                  >
                    <v-icon left x-small>
                      {{ statusConfig[skill.bestStatus].icon }}
                    </v-icon>
                    {{ skill.bestStatus }}
                  </v-chip>
                </v-col>

                <!-- Step progress dots -->
                <v-col cols="auto" class="d-flex align-center mr-3">
                  <div
                    v-for="(step, idx) in skill.steps"
                    :key="`dot-${skill.skill_id}-${idx}`"
                    class="d-flex align-center"
                  >
                    <v-tooltip
                      bottom
                      :color="statusConfig[step.status].chipColor"
                    >
                      <template v-slot:activator="{ on }">
                        <v-icon
                          v-on="on"
                          small
                          :color="
                            step.status !== 'Not Started'
                              ? statusConfig[step.status].color
                              : 'grey lighten-1'
                          "
                        >
                          {{
                            step.status !== "Not Started"
                              ? "mdi-circle"
                              : "mdi-circle-outline"
                          }}
                        </v-icon>
                      </template>
                      <span
                        >{{ step.step_number }}. {{ step.step_name }}:
                        {{ step.status }}</span
                      >
                    </v-tooltip>
                    <div
                      v-if="idx < skill.steps.length - 1"
                      class="step-line"
                      :style="{
                        background:
                          step.status !== 'Not Started'
                            ? statusConfig[step.status].colorHex
                            : '#E0E0E0',
                      }"
                    ></div>
                  </div>
                </v-col>
                <v-col cols="auto">
                  <span class="caption grey--text">
                    {{ skill.completedCount }}/{{ skill.steps.length }}
                  </span>
                </v-col>
              </v-row>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-list dense class="pt-0">
                <v-list-item
                  v-for="(step, idx) in skill.steps"
                  :key="`step-${skill.skill_id}-${idx}`"
                  :class="step.status !== 'Not Started' ? 'completed-step' : ''"
                >
                  <v-list-item-icon class="mr-3">
                    <v-icon :color="statusConfig[step.status].color" small>
                      {{ statusConfig[step.status].icon }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2">
                      <strong>{{ step.step_number }}.</strong>
                      {{ step.step_name }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-if="step.session_date"
                      class="caption"
                    >
                      Session date: {{ step.session_date | dateReOrder }}
                    </v-list-item-subtitle>
                    <!-- Step progress timeline (5 circles: Introduced through Reflection) -->
                    <div class="mt-2 d-flex align-center" style="height: 20px">
                      <div
                        v-for="(status, idx) in progressStatuses.slice(1)"
                        :key="`step-dot-${step.step_id}-${idx}`"
                        class="d-flex align-center"
                      >
                        <v-icon
                          small
                          :color="getProgressDotColor(step.status, status)"
                          :style="{
                            fontSize: '16px',
                          }"
                        >
                          {{
                            isStatusReached(step.status, status)
                              ? "mdi-circle"
                              : "mdi-circle-outline"
                          }}
                        </v-icon>
                        <div
                          v-if="idx < progressStatuses.slice(1).length - 1"
                          style="height: 2px; width: 8px; margin: 0 -2px"
                          :style="{
                            backgroundColor: isStatusReached(
                              step.status,
                              progressStatuses.slice(1)[idx + 1]
                            )
                              ? statusConfig[step.status].colorHex
                              : '#E0E0E0',
                          }"
                        ></div>
                      </div>
                    </div>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip
                      x-small
                      :color="statusConfig[step.status].chipColor"
                      dark
                    >
                      {{ step.status }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>

      <v-alert
        v-else-if="!progressLoading && !groupedProgress.length"
        type="info"
        outlined
        class="mt-4"
      >
        No progress data found for this learner.
      </v-alert>
    </template>

    <v-alert v-else type="info" outlined>
      Please select an enrolment above to view or record progress.
    </v-alert>
  </div>
</template>

<script>
import { required, maxLength } from "vuelidate/lib/validators";
import { mapActions, mapGetters } from "vuex";
import {
  M_OU,
  GET_SYLLABUS_PROGRESS,
  SAVE_SYLLABUS_PROGRESS,
  GET_SKILL_LIST,
  GET_STEP_LIST,
  LOAD_SKILL_LIST,
  LOAD_STEP_LIST,
} from "@/store/mutation-list";

const STATUS_RANK = {
  "Not Started": 0,
  Introduced: 1,
  Helped: 2,
  Prompted: 3,
  Independent: 4,
  Reflection: 5,
};

export default {
  name: "ProgressPanel",

  props: {
    enrollment: { type: Object, default: null },
    enrolments: { type: Array, default: () => [] },
  },

  validations: {
    recordForm: {
      skillId: { required },
      stepId: { required },
      sessionDate: { required },
      status: { required },
      notes: { maxLength: maxLength(500) },
    },
  },

  data() {
    return {
      localEnrolmentId: null,
      progressData: [],
      progressLoading: false,
      sessionDateMenu: false,
      recordSubmitting: false,
      currentLearnerId: null,

      recordForm: {
        skillId: null,
        stepId: null,
        sessionDate: "",
        status: null,
        notes: "",
      },

      statusOptions: [
        "Introduced",
        "Helped",
        "Prompted",
        "Independent",
        "Reflection",
      ],

      progressStatuses: [
        "Not Started",
        "Introduced",
        "Helped",
        "Prompted",
        "Independent",
        "Reflection",
      ],

      statusConfig: {
        "Not Started": {
          color: "grey",
          chipColor: "grey",
          icon: "mdi-minus-circle-outline",
          colorHex: "#9E9E9E",
        },
        Introduced: {
          color: "blue",
          chipColor: "blue",
          icon: "mdi-play-circle",
          colorHex: "#2196F3",
        },
        Helped: {
          color: "orange",
          chipColor: "orange",
          icon: "mdi-hand-back-right",
          colorHex: "#FF9800",
        },
        Prompted: {
          color: "amber darken-2",
          chipColor: "amber darken-2",
          icon: "mdi-comment-question",
          colorHex: "#FFA000",
        },
        Independent: {
          color: "teal",
          chipColor: "teal",
          icon: "mdi-check-circle",
          colorHex: "#009688",
        },
        Reflection: {
          color: "green",
          chipColor: "green",
          icon: "mdi-star-circle",
          colorHex: "#4CAF50",
        },
      },
    };
  },

  computed: {
    ...mapGetters({
      allSkills: `${M_OU}${GET_SKILL_LIST}`,
      allSteps: `${M_OU}${GET_STEP_LIST}`,
    }),

    todayDate() {
      return new Date().toISOString().substr(0, 10);
    },

    activeEnrolment() {
      if (this.enrollment) return this.enrollment;
      if (!this.localEnrolmentId) return null;
      return (
        this.enrolments.find((e) => e.enrolment_id === this.localEnrolmentId) ||
        null
      );
    },

    groupedProgress() {
      if (!this.progressData.length || !this.activeEnrolment) return [];
      // Defensive check: only display data for the current learner
      if (
        this.progressData[0]?.learner_id !== this.activeEnrolment.learner_id
      ) {
        return [];
      }
      const skillMap = new Map();

      this.progressData.forEach((item) => {
        if (!skillMap.has(item.skill_id)) {
          skillMap.set(item.skill_id, {
            skill_id: item.skill_id,
            skill_number: item.skill_number,
            skill_name: item.skill_name,
            steps: [],
          });
        }
        skillMap.get(item.skill_id).steps.push(item);
      });

      return [...skillMap.values()].map((skill) => {
        const completedCount = skill.steps.filter(
          (s) => s.status !== "Not Started"
        ).length;
        const bestStatus = skill.steps.reduce((best, s) => {
          return STATUS_RANK[s.status] > STATUS_RANK[best] ? s.status : best;
        }, "Not Started");

        return { ...skill, completedCount, bestStatus };
      });
    },

    availableSkills() {
      if (!this.activeEnrolment) return [];
      const syllabusId = this.activeEnrolment.syllabus_id;
      return this.allSkills
        .filter((sk) => sk.syllabus_id === syllabusId)
        .map((sk) => ({
          skill_id: sk.skill_id,
          skill_number: sk.skill_number,
          skill_name: sk.skill_name,
        }));
    },

    availableSteps() {
      if (!this.recordForm.skillId) return [];
      // Get all steps for the selected skill from store
      const stepsFromStore = this.allSteps.filter(
        (st) => st.skill_id === this.recordForm.skillId
      );
      // Merge with progress data to include status
      return stepsFromStore.map((st) => {
        const progressEntry = this.progressData.find(
          (p) => p.step_id === st.step_id && p.skill_id === st.skill_id
        );
        return {
          ...st,
          status: progressEntry ? progressEntry.status : "Not Started",
        };
      });
    },

    overallCompleted() {
      return this.progressData.filter((s) => s.status !== "Not Started").length;
    },

    overallTotal() {
      return this.progressData.length;
    },

    // Validation error computed properties
    recordSkillErrors() {
      const errors = [];
      if (!this.$v.recordForm.skillId.$dirty) return errors;
      if (!this.$v.recordForm.skillId.required)
        errors.push("Please select a skill.");
      return errors;
    },

    recordStepErrors() {
      const errors = [];
      if (!this.$v.recordForm.stepId.$dirty) return errors;
      if (!this.$v.recordForm.stepId.required)
        errors.push("Please select a step.");
      return errors;
    },

    recordDateErrors() {
      const errors = [];
      if (!this.$v.recordForm.sessionDate.$dirty) return errors;
      if (!this.$v.recordForm.sessionDate.required)
        errors.push("Session date is required.");
      return errors;
    },

    recordStatusErrors() {
      const errors = [];
      if (!this.$v.recordForm.status.$dirty) return errors;
      if (!this.$v.recordForm.status.required)
        errors.push("Please select a progress status.");
      return errors;
    },

    recordNotesErrors() {
      const errors = [];
      if (!this.$v.recordForm.notes.$dirty) return errors;
      if (!this.$v.recordForm.notes.maxLength)
        errors.push("Notes cannot exceed 500 characters.");
      return errors;
    },
  },

  watch: {
    enrollment: {
      immediate: true,
      handler(val) {
        // Sync dropdown to match prop (e.g., when row clicked in Enrolments tab)
        this.localEnrolmentId = val ? val.enrolment_id : null;
      },
    },

    activeEnrolment(val) {
      this.progressData = [];
      this.resetRecordForm();
      if (val) {
        this.currentLearnerId = val.learner_id;
        this.loadProgress(val.learner_id);
      } else {
        this.currentLearnerId = null;
      }
    },
  },

  methods: {
    ...mapActions({
      fetchProgress: `${M_OU}${GET_SYLLABUS_PROGRESS}`,
      saveProgressAction: `${M_OU}${SAVE_SYLLABUS_PROGRESS}`,
      loadSkills: `${M_OU}${LOAD_SKILL_LIST}`,
      loadSteps: `${M_OU}${LOAD_STEP_LIST}`,
    }),

    showAlert(type, title, message) {
      this.$fire({ title, text: message, type, timer: 3000 });
    },

    loadProgress(learnerId) {
      this.progressLoading = true;
      // Ensure skills and steps are loaded
      if (!this.allSkills.length) this.loadSkills();
      if (!this.allSteps.length) this.loadSteps();

      this.fetchProgress({ learner_id: learnerId })
        .then((data) => {
          // Only update if this is still the current learner (prevents race conditions)
          if (this.currentLearnerId === learnerId) {
            this.progressData = data || [];
          }
        })
        .finally(() => {
          this.progressLoading = false;
        });
    },

    onLocalEnrolmentChange(id) {
      const found = this.enrolments.find((e) => e.enrolment_id === id) || null;
      // Notify parent so selectedEnrolment stays in sync
      this.$emit("select-enrolment", found);
    },

    onRecordSkillChange() {
      this.recordForm.stepId = null;
      this.$v.recordForm.stepId.$reset();
    },

    isStepAtStatus(step, status) {
      return step.status === status;
    },

    isStepAtOrBeforeStatus(step, status) {
      const statusRank = {
        "Not Started": 0,
        Introduced: 1,
        Helped: 2,
        Prompted: 3,
        Independent: 4,
        Reflection: 5,
      };
      return statusRank[step.status] >= statusRank[status];
    },

    isStatusReached(stepStatus, checkStatus) {
      const statusRank = {
        "Not Started": 0,
        Introduced: 1,
        Helped: 2,
        Prompted: 3,
        Independent: 4,
        Reflection: 5,
      };
      return statusRank[stepStatus] >= statusRank[checkStatus];
    },

    getProgressDotColor(stepStatus, checkStatus) {
      if (this.isStatusReached(stepStatus, checkStatus)) {
        return this.statusConfig[stepStatus].color;
      }
      return "grey lighten-1";
    },

    resetRecordForm() {
      this.recordForm = {
        skillId: null,
        stepId: null,
        sessionDate: "",
        status: null,
        notes: "",
      };
      this.$v.recordForm.$reset();
    },

    submitProgress() {
      this.$v.recordForm.$touch();
      if (this.$v.recordForm.$invalid) {
        this.showAlert(
          "error",
          "Validation Error",
          "Please fix all errors before saving."
        );
        return;
      }
      this.recordSubmitting = true;
      this.saveProgressAction({
        syllabus_id: this.activeEnrolment.syllabus_id,
        enrolment_id: this.activeEnrolment.enrolment_id,
        instructor_id: this.activeEnrolment.adi_id,
        step_id: this.recordForm.stepId,
        sessionDate: this.recordForm.sessionDate,
        status: this.recordForm.status,
        notes: this.recordForm.notes,
      })
        .then((res) => {
          if (res === "SUCCESS") {
            this.showAlert(
              "success",
              "Saved",
              "Progress recorded successfully."
            );
            this.resetRecordForm();
            // Reload progress to reflect the update
            this.loadProgress(this.activeEnrolment.learner_id);
          } else {
            this.showAlert(
              "error",
              "Error",
              "Failed to save progress. Please try again."
            );
          }
        })
        .finally(() => {
          this.recordSubmitting = false;
        });
    },
  },
};
</script>

<style scoped>
.step-line {
  width: 16px;
  height: 2px;
  margin: 0 1px;
}

.completed-step {
  background-color: #f9fbe7;
}

.section-subtitle {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 4px 0 0;
}
</style>
